import { useRef, useState } from "react";
import { useLocation, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Turnstile, type TurnstileHandle } from "@/components/Turnstile";
import { signup } from "@/lib/auth";
import logo from "@/assets/Logo.png";
import bgDecorative from "@/assets/bg-decorative.svg";

const signupSchema = z.object({
  workspaceName: z.string().min(1, "Workspace name is required"),
  sector: z.enum(["energy", "agriculture"], { required_error: "Select a sector" }),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
  agreedToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the Terms of Service and Privacy Policy",
  }),
});

type SignupValues = z.infer<typeof signupSchema>;

const inputClassName =
  "h-auto rounded-2xl border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300";
const labelClassName = "text-sm font-semibold leading-5 text-slate-700";

function Signup() {
  const [, setLocation] = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      workspaceName: "",
      sector: undefined,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      agreedToTerms: false,
    },
  });

  async function onSubmit(values: SignupValues) {
    setServerError(null);

    if (!turnstileToken) {
      setServerError("Please complete the verification challenge.");
      return;
    }

    // agreedToTerms is a client-side-only gate (see spec §3) — it's validated
    // by the schema above but never sent to the server.
    const { agreedToTerms: _agreedToTerms, ...signupValues } = values;

    try {
      await signup({ ...signupValues, turnstileToken });
      setLocation("/");
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Something went wrong");
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-4 py-8"
      style={{
        backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-[560px] overflow-hidden rounded-3xl border border-slate-200 bg-card shadow-xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col items-center gap-8 p-16">
            <img className="h-9 w-24 object-contain" src={logo} alt="180Pi" />

            <div className="flex w-full flex-col items-center gap-2">
              <h1 className="text-center text-3xl font-extrabold leading-9 text-primary">Create your account</h1>
              <p className="text-center text-base font-normal leading-6 text-muted-foreground">
                Set up your workspace on 180Pi Climate Risk
              </p>
            </div>

            <div className="flex w-full flex-col gap-5">
              <FormField
                control={form.control}
                name="workspaceName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={labelClassName}>Workspace name</FormLabel>
                    <FormControl>
                      <Input autoComplete="organization" className={inputClassName} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sector"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={labelClassName}>Sector</FormLabel>
                    <FormControl>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 text-sm font-normal text-slate-700">
                          <input
                            type="radio"
                            value="agriculture"
                            checked={field.value === "agriculture"}
                            onChange={() => field.onChange("agriculture")}
                            className="h-4 w-4 accent-primary"
                          />
                          Agriculture
                        </label>
                        <label className="flex items-center gap-2 text-sm font-normal text-slate-700">
                          <input
                            type="radio"
                            value="energy"
                            checked={field.value === "energy"}
                            onChange={() => field.onChange("energy")}
                            className="h-4 w-4 accent-primary"
                          />
                          Energy
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelClassName}>First name</FormLabel>
                      <FormControl>
                        <Input autoComplete="given-name" className={inputClassName} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelClassName}>Last name</FormLabel>
                      <FormControl>
                        <Input autoComplete="family-name" className={inputClassName} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={labelClassName}>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder="name@company.com"
                        className={inputClassName}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={labelClassName}>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        className={inputClassName}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="agreedToTerms"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal leading-snug text-slate-700">
                        I agree to the{" "}
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          Terms of Service and Privacy Policy
                        </a>
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Turnstile
                ref={turnstileRef}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
              />

              {serverError && <p className="text-sm font-medium text-destructive">{serverError}</p>}

              <Button
                type="submit"
                disabled={form.formState.isSubmitting || !turnstileToken}
                className="w-full rounded-2xl py-3 text-sm font-bold shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
              >
                {form.formState.isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Create account"
                )}
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link href="/login" className="text-primary underline-offset-4 hover:underline">
                Log in
              </Link>
            </p>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default Signup;
